import {
  createPublicClient,
  decodeFunctionData,
  http,
  keccak256,
  parseAbiItem,
  toBytes,
  webSocket,
  type Address,
  type Hex,
} from 'viem';
import { arbitrum, arbitrumSepolia } from 'viem/chains';
import { TRADING_ABI } from '../../internal/contract.js';
import { getNetworkConfig } from '../../internal/contracts.js';
import { retryAfterMsFromError } from '../../internal/http.js';
import { buildTrade } from '../../internal/open-builder.js';
import { PRECISION_6, PRECISION_18 } from '../../internal/precision.js';
import { OrderType as TradeOrderType, type OpenTradeParams, type SubmissionResult } from '../../types.js';
import { OstiumSubgraphError, OstiumSubgraphErrorCode } from '../errors.js';
import { EMPTY_PNL, getRolloverAccumulator, getTradePnL } from './calculations.js';
import {
  dropGhostPendingMarketOrders,
  makePendingOrderRegisteredReader,
  type PendingOrderRegisteredReader,
} from './pending-orders.js';
import { formatLeverage, formatTokens, formatUsdc } from './decimal.js';
import {
  formatOpenOrder,
  formatOrder,
  formatPosition,
  pairMaxLeverage,
} from './formatters.js';
import type { OstiumPriceStream } from './pricing.js';
import { normalizePairName } from './pair-names.js';
import type { RawLimit, RawOrder, RawPair, RawTrade } from './raw.js';
import type {
  AccountUpdatesSnapshot,
  AccountUpdatesForTrader,
  CloseExecution,
  OpenOrder,
  Order,
  OrderAction,
  PairPosition,
  PriceData,
  PriceTick,
} from '../types.js';

/**
 * Default subgraph reconciliation cadence. Sub-second confirmations come from
 * the Alchemy event watchers (which also trigger an immediate poll), so the
 * timer poll only needs to catch changes with no watcher — limit fills,
 * partial closes, TP/SL edits, liquidations. Pass `pollIntervalMs` to override.
 */
export const DEFAULT_POLL_INTERVAL_MS = 3000;
const OVERLAY_TTL_MS = 10 * 60 * 1000;

const MARKET_OPEN_ORDER_INITIATED = parseAbiItem(
  'event MarketOpenOrderInitiated(uint256 indexed orderId, address indexed trader, uint16 indexed pairIndex)',
);

const MARKET_OPEN_EXECUTED = parseAbiItem(
  'event MarketOpenExecuted(uint256 indexed orderId, (uint256 collateral, uint192 openPrice, uint192 tp, uint192 sl, address trader, uint32 leverage, uint16 pairIndex, uint8 index, bool buy, bool isDayTrade) t, uint256 priceImpactP, uint256 tradeNotional)',
);

const MARKET_CLOSE_EXECUTED_V2 = parseAbiItem(
  'event MarketCloseExecutedV2(uint256 indexed orderId, uint256 indexed tradeId, uint256 price, uint256 priceImpactP, int256 percentProfit, uint256 usdcSentToTrader, uint256 percentageClosed)',
);

const MARKET_OPEN_ORDER_INITIATED_TOPIC = keccak256(
  toBytes('MarketOpenOrderInitiated(uint256,address,uint16)'),
);
const MARKET_CLOSE_ORDER_INITIATED_TOPIC = keccak256(
  toBytes('MarketCloseOrderInitiated(uint256,uint256,address,uint16)'),
);
const MARKET_CLOSE_ORDER_INITIATED_V2_TOPIC = keccak256(
  toBytes('MarketCloseOrderInitiatedV2(uint256,uint256,address,uint16,uint16)'),
);
const PRICE_REQUESTED_TOPIC = keccak256(
  toBytes('PriceRequested(uint256,bytes32,uint256)'),
);

/**
 * Receipt log topics whose `topic1` is the keeper order id. All are emitted by
 * the Trading contract: the `Market*Initiated` events by current deployments,
 * `PriceRequested` by pre-V2 ones (kept for old receipts, and the only topic
 * here with no indexed trader — see `logMatchesTrader`).
 */
const ORDER_ID_TOPICS = new Set<string>([
  MARKET_OPEN_ORDER_INITIATED_TOPIC,
  MARKET_CLOSE_ORDER_INITIATED_TOPIC,
  MARKET_CLOSE_ORDER_INITIATED_V2_TOPIC,
  PRICE_REQUESTED_TOPIC,
]);
const OPEN_ORDER_ID_TOPICS = new Set<string>([
  MARKET_OPEN_ORDER_INITIATED_TOPIC,
]);
const CLOSE_ORDER_ID_TOPICS = new Set<string>([
  MARKET_CLOSE_ORDER_INITIATED_TOPIC,
  MARKET_CLOSE_ORDER_INITIATED_V2_TOPIC,
]);
const ORDER_ID_TRADER_TOPIC_INDEX = new Map<string, number>([
  [MARKET_OPEN_ORDER_INITIATED_TOPIC, 2],
  [MARKET_CLOSE_ORDER_INITIATED_TOPIC, 3],
  [MARKET_CLOSE_ORDER_INITIATED_V2_TOPIC, 3],
]);
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

export interface RawAccountUpdatesSnapshot {
  orders: RawOrder[];
  limits: RawLimit[];
  trades: RawTrade[];
  /**
   * Recently executed close/liquidation orders. Used to tombstone fast overlay
   * entries whose on-chain close event was missed (e.g. during a WebSocket
   * reconnect) so a closed trade can never linger as a ghost position.
   */
  closedOrders?: RawOrder[];
}

export interface ReceiptLogLike {
  topics?: readonly unknown[];
}

export interface TransactionReceiptLike {
  logs?: readonly ReceiptLogLike[];
  receipt?: {
    logs?: readonly ReceiptLogLike[];
  };
}

export type ReceiptOrderAction = 'any' | 'open' | 'close';

interface Keyed<T> {
  keys: string[];
  value: T;
}

interface KeyedAccountUpdatesSnapshot {
  orders: Array<Keyed<Order>>;
  limits: Array<Keyed<OpenOrder>>;
  trades: AccountTradeItem[];
  closeExecutions: Array<Keyed<CloseExecution>>;
}

interface FastOrderItem extends Keyed<Order> {
  createdAt: number;
}

interface AccountTradeItem extends Keyed<PairPosition> {
  raw: RawTrade;
  confirmation?: AccountTradeConfirmation;
}

interface AccountTradeConfirmation {
  localId?: string;
  status: 'optimistic' | 'initiated' | 'executed' | 'indexed';
  orderId?: string;
  initiatedTx?: string;
  initiatedBlock?: string;
  createdAt?: number;
}

interface FastCloseExecutionItem extends Keyed<CloseExecution> {
  createdAt: number;
}

interface OstiumAccountUpdatesStreamOptions {
  /** One or more trader addresses to stream on this connection. Must be non-empty. */
  users: Address[];
  testnet: boolean;
  alchemyApiKey: string;
  pollIntervalMs?: number;
  fetchRawSnapshot: () => Promise<RawAccountUpdatesSnapshot>;
  fetchRawPairs: () => Promise<RawPair[]>;
  fetchLivePrices: () => Promise<Record<string, PriceData>>;
  openPriceStream: () => PriceStreamSource;
  getRawPairName: (pairId: string) => string | undefined;
  getRawPair: (pairId: string) => RawPair | undefined;
  initialRawPairs?: RawPair[];
  /**
   * Override for the on-chain pending-order lookup used to drop ghost pending
   * market orders. Defaults to `reqID_pendingMarketOrder` on TradingStorage.
   */
  isPendingMarketOrderRegistered?: PendingOrderRegisteredReader;
}

type UpdateHandler = (snapshot: AccountUpdatesSnapshot) => void;
type ErrorHandler = (error: Error) => void;
type PriceStreamSource = Pick<OstiumPriceStream, 'onTick' | 'onSnapshot' | 'onError' | 'close'>;

interface MarketOpenExecutedTrade {
  collateral: bigint;
  openPrice: bigint;
  tp: bigint;
  sl: bigint;
  trader: Address;
  leverage: bigint | number;
  pairIndex: bigint | number;
  index: bigint | number;
  buy: boolean;
  isDayTrade: boolean;
}

interface MarketOpenExecutedLog {
  args: {
    orderId?: bigint;
    t?: MarketOpenExecutedTrade;
    tradeNotional: bigint;
  };
  blockNumber?: bigint;
}

interface MarketOpenInitiatedLog {
  args: {
    orderId?: bigint;
    trader?: Address;
    pairIndex?: bigint | number;
  };
  transactionHash?: Hex;
  blockNumber?: bigint;
}

interface MarketCloseExecutedLog {
  args: {
    orderId?: bigint;
    tradeId?: bigint;
    price?: bigint;
    percentProfit?: bigint;
    usdcSentToTrader?: bigint;
    percentageClosed?: bigint;
  };
  blockNumber?: bigint;
}

interface InitiatedMetadata {
  orderId: string;
  pairId?: string;
  trader?: Address;
  initiatedTx?: string;
  initiatedBlock?: string;
}

function alchemyWsUrl(testnet: boolean, apiKey: string): string {
  const network = testnet ? 'arb-sepolia' : 'arb-mainnet';
  return `wss://${network}.g.alchemy.com/v2/${apiKey}`;
}

function alchemyHttpUrl(testnet: boolean, apiKey: string): string {
  const network = testnet ? 'arb-sepolia' : 'arb-mainnet';
  return `https://${network}.g.alchemy.com/v2/${apiKey}`;
}

function createAlchemyLogClient(testnet: boolean, apiKey: string) {
  const chain = testnet ? arbitrumSepolia : arbitrum;
  return createPublicClient({
    chain,
    pollingInterval: 250,
    transport: webSocket(alchemyWsUrl(testnet, apiKey), {
      // viem defaults reconnect attempts to 5, after which the socket dies
      // permanently and the stream goes silently stale. This stream is
      // long-lived by design — keep retrying forever; missed events during
      // gaps are replayed by sweepMissedEvents over HTTP.
      reconnect: { delay: 2000, attempts: Number.MAX_SAFE_INTEGER },
    }),
  });
}

function createAlchemyBlockClient(testnet: boolean, apiKey: string) {
  const chain = testnet ? arbitrumSepolia : arbitrum;
  return createPublicClient({
    chain,
    // The block number only feeds rollover projection in PnL, which drifts
    // negligibly over seconds — serve the per-poll getBlockNumber from viem's
    // cache instead of hitting Alchemy on every tick.
    cacheTime: 30_000,
    transport: http(alchemyHttpUrl(testnet, apiKey)),
  });
}

type AlchemyLogClient = ReturnType<typeof createAlchemyLogClient>;
type AlchemyBlockClient = ReturnType<typeof createAlchemyBlockClient>;

function currentUnixSeconds(): string {
  return Math.floor(Date.now() / 1000).toString();
}

function topicToOrderId(topic: unknown): string | undefined {
  if (topic === undefined || topic === null) return undefined;
  if (typeof topic === 'bigint') return topic.toString();
  if (typeof topic === 'number' && Number.isFinite(topic)) return Math.trunc(topic).toString();
  if (typeof topic !== 'string' || topic.length === 0) return undefined;

  try {
    return BigInt(topic).toString();
  } catch {
    return topic;
  }
}

function normalizeAddress(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const lower = value.toLowerCase();
  if (!/^0x[0-9a-f]{40}$/.test(lower)) return undefined;
  return lower;
}

function addressFromTopic(topic: unknown): string | undefined {
  if (typeof topic !== 'string') return undefined;
  const lower = topic.toLowerCase();
  if (/^0x[0-9a-f]{40}$/.test(lower)) return lower;
  if (!/^0x[0-9a-f]{64}$/.test(lower)) return undefined;
  return `0x${lower.slice(-40)}`;
}

function logMatchesTrader(topics: readonly unknown[], topic0: string, trader: string | undefined): boolean {
  if (!trader) return true;
  const traderTopicIndex = ORDER_ID_TRADER_TOPIC_INDEX.get(topic0);
  // Fails closed by design. Both PriceRequested variants carry no address at
  // all, so a log using one cannot be attributed to a trader. Passing it through
  // would let a bundled 4337 handleOps receipt return another trader's order id
  // — the exact misattribution the trader filter exists to prevent, and worse
  // than undefined: a wrong id is promoted onto the caller's optimistic overlay,
  // never matches the executed open, and lingers until the overlay TTL. Callers
  // parsing pre-V2 receipts should omit the trader argument instead.
  if (traderTopicIndex === undefined) return false;
  return addressFromTopic(topics[traderTopicIndex]) === trader;
}

/**
 * Extract the keeper order id from a transaction receipt, reading the first
 * matching initiated-event log.
 *
 * @param receipt - A viem receipt, or any object with `logs` / `receipt.logs`.
 * @param trader - Restrict matching to logs whose indexed trader topic equals
 *   this address. Supply it whenever the receipt may bundle more than one
 *   trader's operations (an ERC-4337 `handleOps` receipt), otherwise the
 *   first-match scan can return someone else's order id.
 *
 *   **Only logs that carry an indexed trader topic can match.** The current
 *   contracts' `MarketOpenOrderInitiated`, `MarketCloseOrderInitiated`, and
 *   `MarketCloseOrderInitiatedV2` all qualify. The pre-V2 `PriceRequested`
 *   carries no address, so it is skipped rather than assumed to match — parsing
 *   an old `PriceRequested`-only receipt requires omitting this argument.
 * @param action - Scope the match to an open or a close order id, for receipts
 *   that bundle both (e.g. a close and an open in one `handleOps`), where the
 *   default first-match scan would return whichever the bundler ordered first.
 *   Defaults to `'any'`, which preserves that first-match behavior.
 *
 *   Scoping keys off `topic0` alone. `PriceRequested` carries no action, so it
 *   matches only under `'any'`.
 * @returns The order id as a base-10 string, or `undefined` when no log
 *   satisfies every supplied constraint.
 */
export function extractOrderIdFromReceipt(
  receipt: TransactionReceiptLike | null | undefined,
  trader?: Address | string,
  action: ReceiptOrderAction = 'any',
): string | undefined {
  const logs = receipt?.logs ?? receipt?.receipt?.logs ?? [];
  const normalizedTrader = normalizeAddress(trader);
  if (trader !== undefined && !normalizedTrader) return undefined;
  // Action scoping only targets the split open/close events where topic0 alone
  // is unambiguous. Consolidated/legacy topics remain available via "any".
  const scopedTopics =
    action === 'open'
      ? OPEN_ORDER_ID_TOPICS
      : action === 'close'
        ? CLOSE_ORDER_ID_TOPICS
        : ORDER_ID_TOPICS;
  for (const log of logs) {
    const [topic0, topic1] = log.topics ?? [];
    if (typeof topic0 !== 'string') continue;
    const normalizedTopic0 = topic0.toLowerCase();
    if (!scopedTopics.has(normalizedTopic0)) continue;
    if (!logMatchesTrader(log.topics ?? [], normalizedTopic0, normalizedTrader)) continue;
    const orderId = topicToOrderId(topic1);
    if (orderId === undefined) continue;
    return orderId;
  }
  return undefined;
}

/**
 * Pair rollover accumulator at the trade's open block. `RawTrade.rollover`
 * holds the at-open accumulator snapshot (subgraph convention), so PnL charges
 * only rollover accrued after open — a `'0'` baseline would charge the pair's
 * whole rollover history against a brand-new position.
 */
function rolloverBaselineAtOpen(
  pair: RawPair,
  isBuy: boolean,
  blockNumber: bigint | undefined,
): string {
  if (blockNumber === undefined) return '0';
  try {
    return getRolloverAccumulator(pair, isBuy, blockNumber);
  } catch {
    return '0';
  }
}

function rawPairNameToPriceKey(rawName: string): string | undefined {
  const [from, to] = rawName.split('-');
  if (!from || !to) return undefined;
  return `${from}/${to}`;
}

function rawPairNameToParts(rawName?: string): { from: string; to: string } | undefined {
  if (!rawName) return undefined;
  const [from, to] = rawName.split('-');
  if (!from || !to) return undefined;
  return { from, to };
}

function optimisticRawPair(pairId: string, openPrice: string, rawName?: string): RawPair {
  const parts = rawPairNameToParts(rawName);
  return {
    id: pairId,
    from: parts?.from ?? `PAIR-${pairId}`,
    to: parts?.to ?? 'USD',
    maxLeverage: '0',
    overnightMaxLeverage: '0',
    takerFeeP: '0',
    maxOI: '0',
    longOI: '0',
    shortOI: '0',
    group: {
      id: '0',
      name: '',
      maxLeverage: '0',
    },
    lastUpdateTimestamp: currentUnixSeconds(),
    lastTradePrice: openPrice,
    accRolloverLong: '0',
    accRolloverShort: '0',
    lastRolloverBlock: '0',
    lastRolloverLongPure: '0',
    brokerPremium: '0',
    isNegativeRolloverAllowed: false,
    buyVolume: '0',
    sellVolume: '0',
    decayRate: '0',
    netVolThreshold: '0',
    priceImpactK: '0',
    spreadP: '0',
  };
}

function priceDataFromTick(tick: PriceTick): PriceData {
  return {
    mid: tick.mid,
    bid: tick.bid,
    ask: tick.ask,
    isMarketOpen: tick.isMarketOpen,
    isDayTradingClosed: tick.isDayTradingClosed,
    secondsToToggleIsDayTradingClosed: tick.secondsToToggleIsDayTradingClosed,
  };
}

function normalizeComparableId(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;
  const normalized = value.toString();
  return normalized.length > 0 ? normalized : undefined;
}

function idsMatch(left: unknown, right: unknown): boolean {
  const normalizedLeft = normalizeComparableId(left);
  const normalizedRight = normalizeComparableId(right);
  if (!normalizedLeft || !normalizedRight) return false;
  if (normalizedLeft === normalizedRight) return true;

  try {
    return BigInt(normalizedLeft) === BigInt(normalizedRight);
  } catch {
    return false;
  }
}

function uniqueIds(ids: unknown[]): string[] {
  const unique: string[] = [];
  for (const id of ids) {
    const normalized = normalizeComparableId(id);
    if (!normalized) continue;
    if (unique.some(existing => idsMatch(existing, normalized))) continue;
    unique.push(normalized);
  }
  return unique;
}

function traderPairIndexKey(trader: unknown, pairId: unknown, index: unknown): string | undefined {
  const normalizedTrader = normalizeComparableId(trader)?.toLowerCase();
  const normalizedPairId = normalizeComparableId(pairId);
  const normalizedIndex = normalizeComparableId(index);
  if (!normalizedTrader || !normalizedPairId || !normalizedIndex) return undefined;
  return `trader:${normalizedTrader}:pair:${normalizedPairId}:index:${normalizedIndex}`;
}

function getOrderKeys(order: RawOrder | Order): string[] {
  if ('oid' in order) return uniqueIds([order.oid, order.pid, order.initiatedTx]);
  return uniqueIds([order.id, order.tradeID, order.initiatedTx]);
}

function getLimitKeys(limit: RawLimit | OpenOrder): string[] {
  if ('idx' in limit) return uniqueIds([
    traderPairIndexKey(limit.trader, limit.pairId, limit.idx),
  ]);
  return uniqueIds([
    limit.id,
    limit.uniqueId,
    traderPairIndexKey(limit.trader, limit.pair.id, limit.orderId),
  ]);
}

function getTradeKeys(trade: RawTrade | PairPosition): string[] {
  if ('position' in trade) {
    return uniqueIds([
      trade.position.pid,
      traderPairIndexKey(trade.position.trader, trade.position.pairId, trade.position.idx),
    ]);
  }
  return uniqueIds([
    trade.id,
    trade.tradeID,
    traderPairIndexKey(trade.trader, trade.pair.id, trade.index),
  ]);
}

// Deliberately excludes the bare tradeId: every close of a trade carries the
// opening order's id, so keying on it would make dedupeKeyedItems() collapse
// separate partial closes of the same trade into one execution. orderId is
// unique per execution, and the composite key dedupes the same close arriving
// from both the contract-log overlay and the subgraph.
function getCloseExecutionKeys(execution: CloseExecution): string[] {
  return uniqueIds([
    execution.orderId,
    `${execution.source}:${execution.orderId}:${execution.tradeId}`,
  ]);
}

function tradersMatch(left: unknown, right: unknown): boolean {
  const normalizedLeft = normalizeComparableId(left)?.toLowerCase();
  const normalizedRight = normalizeComparableId(right)?.toLowerCase();
  return !!normalizedLeft && !!normalizedRight && normalizedLeft === normalizedRight;
}

function itemMatchesId<T>(item: Keyed<T>, id: string): boolean {
  return item.keys.some(key => idsMatch(key, id));
}

function dedupeKeyedItems<T extends Keyed<unknown>>(items: T[]): T[] {
  const seenKeys: string[] = [];
  const deduped: T[] = [];

  for (const item of items) {
    const alreadySeen = item.keys.some(key =>
      seenKeys.some(seen => idsMatch(seen, key)),
    );
    if (alreadySeen) continue;
    deduped.push(item);
    seenKeys.push(...item.keys);
  }

  return deduped;
}

function stableFingerprint(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map(item => stableFingerprint(item)).join(',')}]`;
  }

  const entries = Object.entries(value as Record<string, unknown>)
    .sort(([a], [b]) => a.localeCompare(b));
  return `{${entries
    .map(([key, item]) => `${JSON.stringify(key)}:${stableFingerprint(item)}`)
    .join(',')}}`;
}

function normalizedTraderKey(value: unknown): string | undefined {
  return normalizeComparableId(value)?.toLowerCase();
}

function emptyAccountUpdatesForTrader(): AccountUpdatesForTrader {
  return {
    positions: [],
    orders: [],
    limits: [],
    closeExecutions: [],
  };
}

function cloneAccountUpdatesForTrader(group: AccountUpdatesForTrader): AccountUpdatesForTrader {
  return {
    positions: group.positions.map(trade => ({
      position: { ...trade.position },
    })),
    orders: group.orders.map(order => ({
      ...order,
      fees: { ...order.fees },
    })),
    limits: group.limits.map(limit => ({ ...limit })),
    closeExecutions: (group.closeExecutions ?? []).map(execution => ({ ...execution })),
  };
}

function cloneSnapshot(snapshot: AccountUpdatesSnapshot): AccountUpdatesSnapshot {
  return Object.fromEntries(
    Object.entries(snapshot).map(([trader, group]) => [
      trader,
      cloneAccountUpdatesForTrader(group),
    ]),
  );
}

function ensureTraderGroup(
  snapshot: AccountUpdatesSnapshot,
  trader: unknown,
): AccountUpdatesForTrader | undefined {
  const key = normalizedTraderKey(trader);
  if (!key) return undefined;
  snapshot[key] ??= emptyAccountUpdatesForTrader();
  return snapshot[key];
}

function toPublicSnapshot(
  snapshot: KeyedAccountUpdatesSnapshot,
  users: readonly Address[] = [],
): AccountUpdatesSnapshot {
  const grouped: AccountUpdatesSnapshot = {};

  for (const user of users) {
    const key = normalizedTraderKey(user);
    if (key) grouped[key] = emptyAccountUpdatesForTrader();
  }

  for (const item of snapshot.trades) {
    ensureTraderGroup(grouped, item.value.position.trader)?.positions.push(item.value);
  }
  for (const item of snapshot.orders) {
    ensureTraderGroup(grouped, item.value.trader)?.orders.push(item.value);
  }
  for (const item of snapshot.limits) {
    ensureTraderGroup(grouped, item.value.trader)?.limits.push(item.value);
  }
  for (const item of snapshot.closeExecutions) {
    ensureTraderGroup(grouped, item.value.trader)?.closeExecutions?.push(item.value);
  }

  return grouped;
}

function formatTrade(
  raw: RawTrade,
  prices: Record<string, PriceData>,
  blockNumber?: bigint,
): PairPosition {
  const price = prices[`${raw.pair.from}/${raw.pair.to}`];
  const pnl = price && blockNumber ? getTradePnL(raw, price, blockNumber) : EMPTY_PNL;
  const maxLev = raw.isDayTrade
    ? formatLeverage(raw.pair.overnightMaxLeverage)
    : pairMaxLeverage(raw.pair);
  return formatPosition(raw, price, pnl, maxLev);
}

function applyConfirmationMetadata(
  position: PairPosition,
  confirmation?: AccountTradeConfirmation,
): PairPosition {
  if (!confirmation) return position;

  return {
    position: {
      ...position.position,
      confirmationStatus: confirmation.status,
      ...(confirmation.orderId ? { orderId: confirmation.orderId } : {}),
      ...(confirmation.initiatedTx ? { initiatedTx: confirmation.initiatedTx } : {}),
      ...(confirmation.initiatedBlock ? { initiatedBlock: confirmation.initiatedBlock } : {}),
    },
  };
}

function formatConfirmedTrade(
  raw: RawTrade,
  prices: Record<string, PriceData>,
  blockNumber: bigint | undefined,
  confirmation?: AccountTradeConfirmation,
): PairPosition {
  return applyConfirmationMetadata(formatTrade(raw, prices, blockNumber), confirmation);
}

function tradeNotionalFromOpen(pair: Pick<RawPair, 'from'>, collateral: bigint, leverage: bigint, openPrice: bigint): string {
  if (openPrice <= 0n) return '0';

  const notionalUsd = collateral * leverage / 100n;
  if (pair.from === 'USD') {
    return (notionalUsd * openPrice / PRECISION_6).toString();
  }

  return (notionalUsd * PRECISION_18 * PRECISION_18 / (PRECISION_6 * openPrice)).toString();
}

interface DecodedOpenTradeInput {
  buy: boolean;
  collateral: bigint;
  leverage: bigint;
  openPrice: bigint;
}

/**
 * Decode the trade fields from an `openTrade` transaction's calldata.
 * Unwraps one level of `delegatedAction`. Returns undefined for calldata the
 * Trading ABI can't decode (e.g. Safe-batched gasless submissions).
 */
function decodeOpenTradeInput(data: Hex, depth = 0): DecodedOpenTradeInput | undefined {
  try {
    const decoded = decodeFunctionData({ abi: TRADING_ABI, data });
    if (decoded.functionName === 'delegatedAction' && depth === 0) {
      return decodeOpenTradeInput(decoded.args[1] as Hex, depth + 1);
    }
    if (decoded.functionName !== 'openTrade') return undefined;

    const trade = decoded.args[0] as {
      buy: boolean;
      collateral: bigint;
      leverage: number | bigint;
      openPrice: bigint;
    };
    return {
      buy: trade.buy,
      collateral: BigInt(trade.collateral),
      leverage: BigInt(trade.leverage),
      openPrice: BigInt(trade.openPrice),
    };
  } catch {
    return undefined;
  }
}

function rawPendingOrderFromInitiated({
  orderId,
  trader,
  pair,
  initiatedTx,
  initiatedBlock,
}: {
  orderId: string;
  trader: Address;
  pair: RawPair;
  initiatedTx?: string;
  initiatedBlock?: string;
}): RawOrder {
  const initiatedAt = currentUnixSeconds();
  return {
    id: orderId,
    tradeID: orderId,
    trader,
    pair,
    orderAction: 'Open',
    orderType: 'Market',
    // The initiated event carries no direction/size — placeholders below are
    // corrected asynchronously from the transaction calldata when decodable.
    isBuy: true,
    isPending: true,
    isCancelled: false,
    collateral: '0',
    notional: '0',
    tradeNotional: '0',
    leverage: '0',
    price: '0',
    priceAfterImpact: '0',
    priceImpactP: '0',
    vaultFee: '0',
    devFee: '0',
    oracleFee: '0',
    rolloverFee: '0',
    liquidationFee: '0',
    builder: ZERO_ADDRESS,
    builderFee: '0',
    profitPercent: '0',
    totalProfitPercent: '0',
    amountSentToTrader: '0',
    closePercent: '0',
    initiatedTx: initiatedTx ?? '',
    initiatedBlock: initiatedBlock ?? '',
    initiatedAt,
  };
}

function isPendingOrder(order: RawOrder): boolean {
  return order.isPending !== false && order.isCancelled !== true;
}

function isActiveLimit(limit: RawLimit): boolean {
  return limit.isActive !== false;
}

function isOpenTrade(trade: RawTrade): boolean {
  return trade.isOpen !== false;
}

/**
 * Close percentages are 2-decimal fixed point everywhere they enter the SDK:
 * the subgraph mirrors the contract's `closePercentage` arg (see `closeTrade`,
 * which sends `closePercent * 100`) and `MarketCloseExecutedV2.percentageClosed`
 * uses the same scale. A full close is always `10000`, never `100`.
 */
function normalizeClosePercentage(value: unknown): number {
  if (value === undefined || value === null) return 0;
  const raw = Number(value);
  if (!Number.isFinite(raw)) return 0;
  return raw / 100;
}

function isFullClose(order: Pick<RawOrder, 'orderAction' | 'closePercent'>): boolean {
  if (order.orderAction === 'Liquidation') return true;
  return normalizeClosePercentage(order.closePercent) >= 100;
}

function isCloseExecutionOrder(order: RawOrder): boolean {
  return order.orderAction === 'Close' ||
    order.orderAction === 'Liquidation' ||
    order.orderAction === 'StopLoss' ||
    order.orderAction === 'TakeProfit' ||
    order.orderAction === 'CloseDayTrade';
}

function keyedCloseExecution(execution: CloseExecution): Keyed<CloseExecution> {
  return {
    keys: getCloseExecutionKeys(execution),
    value: execution,
  };
}

interface BuildCloseExecutionInput {
  orderId: string;
  tradeId: string;
  trader: string;
  pairId?: string;
  pairTo?: string;
  pairFrom?: string;
  action?: OrderAction;
  pxRaw: string | number | bigint | null | undefined;
  percentProfitRaw: string | number | bigint | null | undefined;
  usdcSentToTraderRaw: string | number | bigint | null | undefined;
  percentageClosedRaw: string | number | bigint | null | undefined;
  isFullClose?: boolean;
  source: 'event' | 'subgraph';
  blockNumber?: unknown;
}

function optionalBlockNumber(value: unknown): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  return typeof value === 'string' ? value : value.toString();
}

function buildCloseExecution(input: BuildCloseExecutionInput): CloseExecution {
  const percentageClosed = normalizeClosePercentage(input.percentageClosedRaw);
  const execution: CloseExecution = {
    orderId: input.orderId,
    tradeId: input.tradeId,
    trader: input.trader,
    pairId: input.pairId,
    pairTo: input.pairTo,
    pairFrom: input.pairFrom,
    action: input.action,
    px: formatTokens(input.pxRaw).toString(),
    percentProfit: formatUsdc(input.percentProfitRaw).toString(),
    usdcSentToTrader: formatUsdc(input.usdcSentToTraderRaw).toString(),
    percentageClosed: percentageClosed.toString(),
    isFullClose: input.isFullClose ?? percentageClosed >= 100,
    source: input.source,
  };
  const blockNumber = optionalBlockNumber(input.blockNumber);
  if (blockNumber !== undefined) execution.blockNumber = blockNumber;
  return execution;
}

function closeExecutionFromOrder(order: RawOrder): CloseExecution {
  const pxRaw = order.priceAfterImpact && order.priceAfterImpact !== '0'
    ? order.priceAfterImpact
    : order.price;
  return buildCloseExecution({
    orderId: topicToOrderId(order.id) ?? '',
    tradeId: topicToOrderId(order.tradeID) ?? topicToOrderId(order.id) ?? '',
    trader: order.trader.toLowerCase(),
    pairId: String(order.pair.id),
    pairTo: normalizePairName(order.pair.to),
    pairFrom: normalizePairName(order.pair.from),
    action: order.orderAction as OrderAction,
    pxRaw,
    percentProfitRaw: order.profitPercent,
    usdcSentToTraderRaw: order.amountSentToTrader,
    percentageClosedRaw: order.closePercent,
    isFullClose: isFullClose(order),
    source: 'subgraph',
    blockNumber: order.executedBlock,
  });
}

function formatKeyedAccountUpdatesSnapshot(
  raw: RawAccountUpdatesSnapshot,
  prices: Record<string, PriceData>,
  blockNumber?: bigint,
): KeyedAccountUpdatesSnapshot {
  return {
    orders: raw.orders
      .filter(isPendingOrder)
      .map(order => ({ keys: getOrderKeys(order), value: formatOrder(order) })),
    limits: raw.limits
      .filter(isActiveLimit)
      .map(limit => ({ keys: getLimitKeys(limit), value: formatOpenOrder(limit) })),
    trades: raw.trades
      .filter(isOpenTrade)
      .map(trade => ({
        keys: getTradeKeys(trade),
        raw: trade,
        value: formatTrade(trade, prices, blockNumber),
      })),
    closeExecutions: (raw.closedOrders ?? [])
      .filter(isCloseExecutionOrder)
      .map(order => keyedCloseExecution(closeExecutionFromOrder(order))),
  };
}

export function formatAccountUpdatesSnapshot(
  raw: RawAccountUpdatesSnapshot,
  prices: Record<string, PriceData>,
  blockNumber?: bigint,
): AccountUpdatesSnapshot {
  return toPublicSnapshot(formatKeyedAccountUpdatesSnapshot(raw, prices, blockNumber));
}

class AccountUpdatesState {
  private fingerprint = '';
  /** No emissions until the first subgraph snapshot lands — prevents a misleading empty first emit. */
  private ready = false;
  private subgraphSnapshot: KeyedAccountUpdatesSnapshot = {
    orders: [],
    limits: [],
    trades: [],
    closeExecutions: [],
  };
  private fastOrders = new Map<string, FastOrderItem>();
  private fastTrades = new Map<string, AccountTradeItem>();
  private fastCloseExecutions = new Map<string, FastCloseExecutionItem>();
  private hiddenOrderIds = new Map<string, number>();
  private closedTradeIds = new Map<string, number>();

  constructor(
    private readonly onChange: (snapshot: AccountUpdatesSnapshot) => void,
    private readonly users: readonly Address[],
  ) {}

  ingest(snapshot: KeyedAccountUpdatesSnapshot): void {
    this.subgraphSnapshot = snapshot;
    for (const close of snapshot.closeExecutions) {
      if (close.value.isFullClose) this.recordTradeClosed(close.value.tradeId);
    }
    this.ready = true;
    this.reconcileFastState();
    this.emitIfChanged();
  }

  upsertFastTrade(trade: AccountTradeItem, orderId?: string): void {
    const key = trade.keys[0];
    if (!key) return;

    if (orderId) {
      this.hideOrder(orderId);
      this.deleteFastOrder(orderId);
      this.deleteFastTrades(orderId);
    }
    for (const tradeKey of trade.keys) this.closedTradeIds.delete(tradeKey);
    this.fastTrades.set(key, trade);
    this.emitIfChanged();
  }

  attachOrderId(localId: string, metadata: InitiatedMetadata): boolean {
    for (const [key, trade] of this.fastTrades) {
      if (key !== localId && !itemMatchesId(trade, localId)) continue;

      const promoted = this.promotedTrade(trade, metadata);
      this.fastTrades.delete(key);
      this.fastTrades.set(promoted.keys[0], promoted);
      this.hideOrder(metadata.orderId);
      this.deleteFastOrder(metadata.orderId);
      this.emitIfChanged();
      return true;
    }

    return false;
  }

  promoteOptimisticTradeByPair(pairId: string, metadata: InitiatedMetadata): boolean {
    for (const [key, trade] of this.fastTrades) {
      if (trade.confirmation?.status !== 'optimistic') continue;
      if (String(trade.raw.pair.id) !== String(pairId)) continue;
      if (metadata.trader && !tradersMatch(trade.raw.trader, metadata.trader)) continue;

      const promoted = this.promotedTrade(trade, metadata);
      this.fastTrades.delete(key);
      this.fastTrades.set(promoted.keys[0], promoted);
      this.hideOrder(metadata.orderId);
      this.deleteFastOrder(metadata.orderId);
      this.emitIfChanged();
      return true;
    }

    return false;
  }

  upsertFastOrder(order: FastOrderItem): void {
    const key = order.keys[0];
    if (!key || this.hasHiddenOrder(order)) return;

    this.fastOrders.set(key, order);
    this.emitIfChanged();
  }

  hasFastOrder(orderId: string): boolean {
    for (const [key, order] of this.fastOrders) {
      if (idsMatch(key, orderId) || itemMatchesId(order, orderId)) return true;
    }
    return false;
  }

  /** Whether a close tombstone exists for this trade id. */
  isTradeClosed(tradeId: string): boolean {
    const normalized = normalizeComparableId(tradeId);
    if (!normalized) return false;
    return [...this.closedTradeIds.keys()].some(id => idsMatch(id, normalized));
  }

  findTradeContext(tradeId: string): {
    tradeId: string;
    trader: string;
    pairId: string;
    idx: number;
    pairTo: string;
    pairFrom: string;
  } | undefined {
    const normalizedTradeId = normalizeComparableId(tradeId);
    if (!normalizedTradeId) return undefined;

    for (const trade of [...this.fastTrades.values(), ...this.subgraphSnapshot.trades]) {
      if (!itemMatchesId(trade, normalizedTradeId)) continue;
      return {
        tradeId: trade.value.position.pid,
        trader: trade.value.position.trader,
        pairId: trade.value.position.pairId,
        idx: trade.value.position.idx,
        pairTo: trade.value.position.pairTo,
        pairFrom: trade.value.position.pairFrom,
      };
    }

    return undefined;
  }

  upsertCloseExecution(execution: CloseExecution): void {
    const item: FastCloseExecutionItem = {
      ...keyedCloseExecution(execution),
      createdAt: Date.now(),
    };
    const key = item.keys[0];
    if (!key) return;

    this.fastCloseExecutions.set(key, item);
    if (execution.isFullClose) this.recordTradeClosed(execution.tradeId);
    this.emitIfChanged();
  }

  markTradeClosed(tradeId: string): void {
    this.recordTradeClosed(tradeId);
    this.emitIfChanged();
  }

  private recordTradeClosed(tradeId: string): void {
    const normalizedTradeId = normalizeComparableId(tradeId);
    if (!normalizedTradeId) return;

    this.closedTradeIds.set(normalizedTradeId, Date.now());
    for (const [key, trade] of this.fastTrades) {
      if (idsMatch(key, normalizedTradeId) || itemMatchesId(trade, normalizedTradeId)) {
        this.fastTrades.delete(key);
      }
    }
  }

  getCurrent(): AccountUpdatesSnapshot {
    return cloneSnapshot(toPublicSnapshot(this.getMergedSnapshot(), this.users));
  }

  hasTradeForPair(pairId: string): boolean {
    return this.getMergedSnapshot().trades.some(
      trade => String(trade.raw.pair.id) === String(pairId),
    );
  }

  repriceTrades(
    prices: Record<string, PriceData>,
    blockNumber?: bigint,
    pairIds?: Set<string>,
  ): void {
    const reprice = (trade: AccountTradeItem): AccountTradeItem => {
      if (pairIds && !pairIds.has(String(trade.raw.pair.id))) {
        return trade;
      }
      return {
        ...trade,
        value: formatConfirmedTrade(trade.raw, prices, blockNumber, trade.confirmation),
      };
    };

    this.subgraphSnapshot = {
      ...this.subgraphSnapshot,
      trades: this.subgraphSnapshot.trades.map(reprice),
    };
    this.fastTrades = new Map(
      [...this.fastTrades.entries()].map(([key, trade]) => [key, reprice(trade)]),
    );
    this.emitIfChanged();
  }

  private getMergedSnapshot(): KeyedAccountUpdatesSnapshot {
    const activeFastOrders = [...this.fastOrders.values()]
      .filter(order => !this.hasHiddenOrder(order));
    const activeOrders = this.subgraphSnapshot.orders
      .filter(order => !this.hasHiddenOrder(order));
    const activeTrades = this.subgraphSnapshot.trades
      .filter(trade => !this.hasFastClose(trade));
    const activeFastCloseExecutions = [...this.fastCloseExecutions.values()];

    return {
      orders: dedupeKeyedItems([...activeFastOrders, ...activeOrders]),
      limits: dedupeKeyedItems(this.subgraphSnapshot.limits),
      trades: dedupeKeyedItems([...this.fastTrades.values(), ...activeTrades]),
      closeExecutions: dedupeKeyedItems([
        ...activeFastCloseExecutions,
        ...this.subgraphSnapshot.closeExecutions,
      ]),
    };
  }

  private reconcileFastState(): void {
    this.pruneTimedIds();
    const indexedOrderKeys = this.subgraphSnapshot.orders.flatMap(order => order.keys);
    const indexedTradeKeys = this.subgraphSnapshot.trades.flatMap(trade => trade.keys);

    for (const [key, order] of this.fastOrders) {
      const isIndexedOrder = order.keys.some(orderKey =>
        indexedOrderKeys.some(indexedKey => idsMatch(indexedKey, orderKey)),
      );
      const isIndexedTrade = order.keys.some(orderKey =>
        indexedTradeKeys.some(indexedKey => idsMatch(indexedKey, orderKey)),
      );
      if (
        isIndexedOrder ||
        isIndexedTrade ||
        indexedOrderKeys.some(indexedKey => idsMatch(indexedKey, key)) ||
        indexedTradeKeys.some(indexedKey => idsMatch(indexedKey, key))
      ) {
        this.fastOrders.delete(key);
      }
    }

    for (const [key, trade] of this.fastTrades) {
      const isIndexed = trade.keys.some(tradeKey =>
        indexedTradeKeys.some(indexedKey => idsMatch(indexedKey, tradeKey)),
      );
      if (isIndexed || indexedTradeKeys.some(indexedKey => idsMatch(indexedKey, key))) {
        this.fastTrades.delete(key);
      }
    }
  }

  private hideOrder(orderId: string): void {
    this.hiddenOrderIds.set(orderId, Date.now());
    this.deleteFastOrder(orderId);
  }

  private hasHiddenOrder(order: Keyed<Order>): boolean {
    return [...this.hiddenOrderIds.keys()].some(orderId => itemMatchesId(order, orderId));
  }

  private hasFastClose(trade: Keyed<PairPosition>): boolean {
    return [...this.closedTradeIds.keys()].some(tradeId => itemMatchesId(trade, tradeId));
  }

  private pruneTimedIds(): void {
    const expireBefore = Date.now() - OVERLAY_TTL_MS;
    for (const [id, timestamp] of this.hiddenOrderIds) {
      if (timestamp < expireBefore) this.hiddenOrderIds.delete(id);
    }
    for (const [id, timestamp] of this.closedTradeIds) {
      if (timestamp < expireBefore) this.closedTradeIds.delete(id);
    }
    for (const [key, order] of this.fastOrders) {
      if (order.createdAt < expireBefore) this.fastOrders.delete(key);
    }
    for (const [key, execution] of this.fastCloseExecutions) {
      if (execution.createdAt < expireBefore) this.fastCloseExecutions.delete(key);
    }
    for (const [key, trade] of this.fastTrades) {
      const createdAt = trade.confirmation?.createdAt;
      // 'executed' entries expire too: an executed trade the subgraph never
      // confirms within the TTL is a ghost (its close was likely missed while
      // it was still unindexed), and keeping it forever shows the builder a
      // position that no longer exists.
      if (createdAt !== undefined && createdAt < expireBefore) {
        this.fastTrades.delete(key);
      }
    }
  }

  private promotedTrade(trade: AccountTradeItem, metadata: InitiatedMetadata): AccountTradeItem {
    const confirmation: AccountTradeConfirmation = {
      ...trade.confirmation,
      status: 'initiated',
      orderId: metadata.orderId,
      initiatedTx: metadata.initiatedTx ?? trade.confirmation?.initiatedTx,
      initiatedBlock: metadata.initiatedBlock ?? trade.confirmation?.initiatedBlock,
      createdAt: trade.confirmation?.createdAt ?? Date.now(),
    };
    const raw: RawTrade = {
      ...trade.raw,
      id: metadata.orderId,
      tradeID: metadata.orderId,
      index: '-1',
    };
    const value = applyConfirmationMetadata(
      {
        position: {
          ...trade.value.position,
          pid: metadata.orderId,
          idx: -1,
        },
      },
      confirmation,
    );

    return {
      keys: uniqueIds([
        trade.confirmation?.localId,
        metadata.orderId,
      ]),
      raw,
      value,
      confirmation,
    };
  }

  private deleteFastOrder(orderId: string): void {
    for (const [key, order] of this.fastOrders) {
      if (idsMatch(key, orderId) || itemMatchesId(order, orderId)) {
        this.fastOrders.delete(key);
      }
    }
  }

  private deleteFastTrades(orderId: string): void {
    for (const [key, trade] of this.fastTrades) {
      if (idsMatch(key, orderId) || itemMatchesId(trade, orderId)) {
        this.fastTrades.delete(key);
      }
    }
  }

  private emitIfChanged(): void {
    if (!this.ready) return;

    const snapshot = toPublicSnapshot(this.getMergedSnapshot(), this.users);
    const nextFingerprint = stableFingerprint(snapshot);
    if (nextFingerprint === this.fingerprint) return;

    this.fingerprint = nextFingerprint;
    this.onChange(cloneSnapshot(snapshot));
  }
}

export class OstiumAccountUpdatesStream {
  private readonly updateHandlers: Set<UpdateHandler> = new Set();
  private readonly errorHandlers: Set<ErrorHandler> = new Set();
  private readonly logClient: AlchemyLogClient;
  private readonly blockClient: AlchemyBlockClient;
  private readonly isPendingMarketOrderRegistered: PendingOrderRegisteredReader;
  private readonly priceStream: PriceStreamSource;
  private readonly state: AccountUpdatesState;
  private readonly unwatchers: Array<() => void> = [];
  private readonly timer: NodeJS.Timeout;
  /** Lower-cased set of subscribed trader addresses, for membership checks. */
  private readonly normalizedUsers: Set<string>;
  /** First subscribed address — used as the attribution fallback when an event omits the trader. */
  private readonly defaultUser: Address;
  private readonly priceCache: Record<string, PriceData> = {};
  private rawPairById: Map<string, RawPair> | null = null;
  private rawPairsPromise: Promise<void> | null = null;
  private latestBlockNumber: bigint | undefined;
  /** Chain head up to which missed-event backfill has run. */
  private lastSweptBlock: bigint | null = null;
  private sweepInFlight = false;
  private optimisticOpenCounter = 0;
  private inFlight = false;
  private consecutivePollFailures = 0;
  private pollBackoffTicksRemaining = 0;
  private pollBackoffUntilMs = 0;
  private closed = false;

  constructor(private readonly options: OstiumAccountUpdatesStreamOptions) {
    this.logClient = createAlchemyLogClient(options.testnet, options.alchemyApiKey);
    this.blockClient = createAlchemyBlockClient(options.testnet, options.alchemyApiKey);
    this.isPendingMarketOrderRegistered = options.isPendingMarketOrderRegistered
      ?? makePendingOrderRegisteredReader(
        this.blockClient,
        getNetworkConfig(options.testnet).contracts.tradingStorage,
      );
    if (options.users.length === 0) {
      throw new OstiumSubgraphError(
        'OstiumAccountUpdatesStream requires at least one trader address.',
        OstiumSubgraphErrorCode.INVALID_PARAMS,
      );
    }
    this.normalizedUsers = new Set(options.users.map(user => user.toLowerCase()));
    this.defaultUser = options.users[0];
    this.state = new AccountUpdatesState(snapshot => this.emit(snapshot), options.users);
    if (options.initialRawPairs?.length) {
      this.rawPairById = new Map(options.initialRawPairs.map(pair => [String(pair.id), pair]));
    }

    this.startWatchers();
    void this.loadRawPairs();
    this.priceStream = options.openPriceStream();
    this.startPriceStream();
    void this.seedPrices();
    void this.poll();
    this.timer = setInterval(
      () => { void this.poll(); },
      options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS,
    );
  }

  onUpdate(handler: UpdateHandler): () => void {
    this.updateHandlers.add(handler);
    return () => { this.updateHandlers.delete(handler); };
  }

  onError(handler: ErrorHandler): this {
    this.errorHandlers.add(handler);
    return this;
  }

  getCurrent(): AccountUpdatesSnapshot {
    return this.state.getCurrent();
  }

  /** Trader addresses this stream is subscribed to, in the order supplied. */
  get users(): Address[] {
    return [...this.options.users];
  }

  addOptimisticOpen(params: OpenTradeParams, submission?: SubmissionResult, user?: Address): string {
    if (params.type !== TradeOrderType.Market) {
      throw new OstiumSubgraphError(
        'addOptimisticOpen only supports market open orders.',
        OstiumSubgraphErrorCode.INVALID_PARAMS,
      );
    }

    const trader = this.resolveOptimisticUser(user);
    const localId = `optimistic:${Date.now()}:${++this.optimisticOpenCounter}`;
    try {
      this.state.upsertFastTrade(this.optimisticTradeFromOpenParams(localId, params, submission, trader));
    } catch (error) {
      if (error instanceof OstiumSubgraphError) throw error;
      throw new OstiumSubgraphError(
        `Failed to add optimistic open: ${error instanceof Error ? error.message : String(error)}`,
        OstiumSubgraphErrorCode.INVALID_PARAMS,
        error,
      );
    }
    return localId;
  }

  attachOrderId(
    localId: string,
    orderId: string,
    metadata?: { initiatedTx?: string; initiatedBlock?: string },
  ): void {
    const normalizedOrderId = normalizeComparableId(orderId);
    if (!localId || !normalizedOrderId) {
      throw new OstiumSubgraphError(
        'attachOrderId requires both localId and orderId.',
        OstiumSubgraphErrorCode.INVALID_PARAMS,
      );
    }

    this.state.attachOrderId(localId, {
      orderId: normalizedOrderId,
      initiatedTx: metadata?.initiatedTx,
      initiatedBlock: metadata?.initiatedBlock,
    });
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    clearInterval(this.timer);
    for (const unwatch of this.unwatchers.splice(0)) unwatch();
    this.priceStream.close();
  }

  private startPriceStream(): void {
    this.priceStream.onSnapshot(ticks => {
      this.ingestPriceTicks(ticks);
    });
    this.priceStream.onTick(tick => {
      this.ingestPriceTicks([tick]);
    });
    this.priceStream.onError(error => this.emitError(error));
  }

  private async seedPrices(): Promise<void> {
    try {
      const prices = await this.options.fetchLivePrices();
      if (this.closed) return;
      this.mergePrices(prices);
      this.state.repriceTrades(this.priceCache, this.latestBlockNumber);
    } catch (error) {
      this.emitError(error);
    }
  }

  private startWatchers(): void {
    const network = getNetworkConfig(this.options.testnet);
    const trading = network.contracts.trading;
    const tradingCallbacks = network.contracts.tradingCallbacks;

    this.unwatchers.push(
      this.logClient.watchEvent({
        address: trading,
        event: MARKET_OPEN_ORDER_INITIATED,
        // viem treats an array on an indexed arg as an OR filter — one watcher
        // covers every subscribed trader.
        args: { trader: this.options.users },
        poll: false,
        onLogs: logs => {
          for (const log of logs) {
            try {
              this.handleMarketOpenInitiated(log as MarketOpenInitiatedLog);
            } catch (error) {
              this.emitError(error);
            }
          }
          void this.poll();
        },
        onError: error => this.emitError(error),
      }),
    );

    this.unwatchers.push(
      this.logClient.watchEvent({
        address: tradingCallbacks,
        event: MARKET_OPEN_EXECUTED,
        poll: false,
        onLogs: logs => {
          for (const log of logs) {
            try {
              this.handleMarketOpenExecuted(log as MarketOpenExecutedLog);
            } catch (error) {
              this.emitError(error);
            }
          }
        },
        onError: error => this.emitError(error),
      }),
    );

    this.unwatchers.push(
      this.logClient.watchEvent({
        address: tradingCallbacks,
        event: MARKET_CLOSE_EXECUTED_V2,
        poll: false,
        onLogs: logs => {
          for (const log of logs) {
            try {
              this.handleMarketCloseExecuted(log as MarketCloseExecutedLog);
            } catch (error) {
              this.emitError(error);
            }
          }
        },
        onError: error => this.emitError(error),
      }),
    );
  }

  private async poll(): Promise<void> {
    if (this.closed || this.inFlight) return;
    // Backoff throttles the subgraph snapshot only. The chain-log sweep talks to
    // a different upstream and is the fallback while the subgraph is degraded, so
    // it still runs on skipped ticks.
    if (this.shouldSkipPollForBackoff()) {
      await this.trackChainTip();
      if (this.closed) return;
      void this.sweepMissedEvents();
      return;
    }
    this.inFlight = true;

    // Started before the snapshot fetch so the two run concurrently, but kept off
    // its promise chain: the sweep below is the safety net for exactly the case
    // where the subgraph is failing, and it no-ops without a tip. Sharing a
    // Promise.all with fetchRawSnapshot() meant a snapshot rejection discarded an
    // already-fetched block number too.
    const tipTracked = this.trackChainTip();

    try {
      const raw = await this.options.fetchRawSnapshot();
      if (this.closed) return;
      // Ghost pending market orders (subgraph missed the terminal event, chain
      // has nothing to cancel) are dropped; stale-but-registered ones stay
      // visible so the user cancels them and frees their pending-order slots.
      const orders = await dropGhostPendingMarketOrders(raw.orders, this.isPendingMarketOrderRegistered);
      if (this.closed) return;
      await tipTracked;
      if (this.closed) return;
      this.state.ingest(
        formatKeyedAccountUpdatesSnapshot({ ...raw, orders }, this.priceCache, this.latestBlockNumber),
      );
      this.resetPollBackoff();
    } catch (error) {
      this.recordPollFailure(error);
      this.emitError(error);
    } finally {
      this.inFlight = false;
    }

    // Unconditional. The sweep reads logs straight from the chain RPC and needs
    // nothing from the subgraph, so it is the only backfill path when the poll
    // fails and a WebSocket gap dropped a MarketCloseExecutedV2. Gating it on
    // poll success left closed trades as ghost open positions for the whole
    // outage. Its own RPC cost stays bounded by the poll interval, the
    // sweepInFlight guard, and the 5000-block range cap.
    await tipTracked;
    if (this.closed) return;
    void this.sweepMissedEvents();
  }

  /**
   * Refresh the cached chain tip, which feeds rollover projection in PnL and is
   * the starting point for `sweepMissedEvents()`. A failure keeps the previous
   * tip rather than clearing it — the tip is already served from a 30s cache, and
   * both consumers prefer a slightly stale tip to none at all.
   */
  private async trackChainTip(): Promise<void> {
    try {
      const tip = await this.blockClient.getBlockNumber();
      if (!this.closed) this.latestBlockNumber = tip;
    } catch {
      // Best effort — the next tick retries.
    }
  }

  private shouldSkipPollForBackoff(): boolean {
    if (this.pollBackoffUntilMs > Date.now()) return true;
    if (this.pollBackoffTicksRemaining <= 0) return false;
    this.pollBackoffTicksRemaining -= 1;
    return true;
  }

  private resetPollBackoff(): void {
    this.consecutivePollFailures = 0;
    this.pollBackoffTicksRemaining = 0;
    this.pollBackoffUntilMs = 0;
  }

  private recordPollFailure(error: unknown): void {
    this.consecutivePollFailures += 1;
    const ticks = Math.min(10, 2 ** Math.max(0, this.consecutivePollFailures - 1));
    this.pollBackoffTicksRemaining = Math.max(this.pollBackoffTicksRemaining, ticks);

    const retryAfterMs = retryAfterMsFromError(error);
    if (retryAfterMs !== undefined) {
      this.pollBackoffUntilMs = Math.max(this.pollBackoffUntilMs, Date.now() + retryAfterMs);
    }
  }

  /**
   * Replay contract events the WebSocket may have dropped. viem reconnects a
   * failed socket and re-subscribes, but logs emitted during the gap are lost;
   * this sweep fetches the gap range over HTTP each poll so a flap degrades
   * confirmation latency to roughly one poll interval instead of losing the
   * event entirely.
   */
  private async sweepMissedEvents(): Promise<void> {
    if (this.closed || this.sweepInFlight) return;
    const tip = this.latestBlockNumber;
    if (tip === undefined) return;
    if (this.lastSweptBlock === null) {
      // First sighting of the chain head — sweep only from here onward.
      this.lastSweptBlock = tip;
      return;
    }
    // Cap the range so a long stall (laptop sleep, extended outage) does not
    // trigger an unbounded scan; older misses are covered by the subgraph
    // poll plus the closedOrders tombstones.
    const maxRange = 5000n;
    const fromBlock = tip - this.lastSweptBlock > maxRange ? tip - maxRange : this.lastSweptBlock + 1n;

    this.sweepInFlight = true;
    try {
      const network = getNetworkConfig(this.options.testnet);
      // `toBlock: 'latest'` rather than the (up to 30s stale) cached tip, so a
      // dropped event surfaces within roughly one poll interval.
      const logs = await this.blockClient.getLogs({
        address: network.contracts.tradingCallbacks,
        events: [MARKET_OPEN_EXECUTED, MARKET_CLOSE_EXECUTED_V2],
        fromBlock,
        toBlock: 'latest',
      });
      if (this.closed) return;

      let sweptTo = tip > this.lastSweptBlock ? tip : this.lastSweptBlock;
      for (const log of logs) {
        if (log.blockNumber !== null && log.blockNumber > sweptTo) sweptTo = log.blockNumber;
        if (log.eventName === 'MarketCloseExecutedV2') {
          this.handleMarketCloseExecuted(log as unknown as MarketCloseExecutedLog);
        } else if (log.eventName === 'MarketOpenExecuted') {
          const trade = log.args.t;
          if (!trade || !this.normalizedUsers.has(trade.trader.toLowerCase())) continue;
          // Ranges overlap across sweeps — never resurrect a tombstoned trade.
          if (this.state.isTradeClosed(log.args.orderId?.toString() ?? '')) continue;
          this.handleMarketOpenExecuted(log as unknown as MarketOpenExecutedLog);
        }
      }
      this.lastSweptBlock = sweptTo;
    } catch {
      // Best effort — the next poll retries the same range.
    } finally {
      this.sweepInFlight = false;
    }
  }

  private ingestPriceTicks(ticks: PriceTick[]): void {
    if (this.closed || ticks.length === 0) return;

    const relevantPairIds = new Set<string>();
    let repriceAll = false;
    for (const tick of ticks) {
      const priceKey = this.priceKeyFromTick(tick);
      if (!priceKey) continue;
      this.priceCache[priceKey] = priceDataFromTick(tick);

      if (tick.pairId && this.state.hasTradeForPair(String(tick.pairId))) {
        relevantPairIds.add(String(tick.pairId));
      } else if (!tick.pairId) {
        repriceAll = true;
      }
    }

    if (repriceAll) {
      this.state.repriceTrades(this.priceCache, this.latestBlockNumber);
    } else if (relevantPairIds.size > 0) {
      this.state.repriceTrades(this.priceCache, this.latestBlockNumber, relevantPairIds);
    }
  }

  private mergePrices(prices: Record<string, PriceData>): void {
    for (const [key, price] of Object.entries(prices)) {
      this.priceCache[key] = price;
    }
  }

  private priceKeyFromTick(tick: PriceTick): string | undefined {
    if (tick.pairId) {
      const priceKey = this.priceKeyFromPairId(String(tick.pairId));
      if (priceKey) return priceKey;
    }

    return `${tick.from}/${tick.to}`;
  }

  private priceKeyFromPairId(pairId: string): string | undefined {
    const rawName = this.options.getRawPairName(pairId);
    if (rawName) return rawPairNameToPriceKey(rawName);

    const rawPair = this.rawPairById?.get(pairId);
    return rawPair ? `${rawPair.from}/${rawPair.to}` : undefined;
  }

  private handleMarketOpenInitiated(log: MarketOpenInitiatedLog): void {
    const eventTrader = log.args.trader;
    if (eventTrader && !this.normalizedUsers.has(eventTrader.toLowerCase())) return;

    const orderId = log.args.orderId?.toString();
    const pairId = log.args.pairIndex?.toString();
    if (!orderId || !pairId) return;

    this.latestBlockNumber = log.blockNumber ?? this.latestBlockNumber;
    const metadata: InitiatedMetadata = {
      orderId,
      pairId,
      trader: eventTrader,
      initiatedTx: log.transactionHash,
      initiatedBlock: log.blockNumber?.toString(),
    };

    if (this.state.promoteOptimisticTradeByPair(pairId, metadata)) return;

    const pair = this.getRawPair(pairId) ?? optimisticRawPair(pairId, '0', this.options.getRawPairName(pairId));
    const rawOrder = rawPendingOrderFromInitiated({
      orderId,
      trader: eventTrader ?? this.defaultUser,
      pair,
      initiatedTx: log.transactionHash,
      initiatedBlock: log.blockNumber?.toString(),
    });
    this.state.upsertFastOrder({
      keys: getOrderKeys(rawOrder),
      value: {
        ...formatOrder(rawOrder),
        initiatedBlock: log.blockNumber?.toString(),
      },
      createdAt: Date.now(),
    });
    void this.enrichPendingOrderFromTx(orderId, log.transactionHash, rawOrder);

    if (!this.getRawPair(pairId)) {
      void this.loadRawPairs().then(() => {
        if (!this.closed) void this.poll();
      });
    }
  }

  /**
   * Replace the placeholder direction/size of a fast pending order with the
   * real values decoded from the initiating transaction's calldata. Best
   * effort — undecodable calldata keeps the placeholder until the subgraph
   * indexes the order.
   */
  private async enrichPendingOrderFromTx(
    orderId: string,
    txHash: Hex | undefined,
    placeholder: RawOrder,
  ): Promise<void> {
    if (!txHash) return;

    try {
      const tx = await this.blockClient.getTransaction({ hash: txHash });
      const open = decodeOpenTradeInput(tx.input);
      if (!open || this.closed || !this.state.hasFastOrder(orderId)) return;

      const enriched: RawOrder = {
        ...placeholder,
        isBuy: open.buy,
        collateral: open.collateral.toString(),
        notional: (open.collateral * open.leverage / 100n).toString(),
        tradeNotional: tradeNotionalFromOpen(
          placeholder.pair,
          open.collateral,
          open.leverage,
          open.openPrice,
        ),
        leverage: open.leverage.toString(),
        price: open.openPrice.toString(),
      };
      this.state.upsertFastOrder({
        keys: getOrderKeys(enriched),
        value: {
          ...formatOrder(enriched),
          initiatedBlock: placeholder.initiatedBlock || undefined,
        },
        createdAt: Date.now(),
      });
    } catch {
      // Best effort — the subgraph snapshot corrects the order once indexed.
    }
  }

  private handleMarketCloseExecuted(log: MarketCloseExecutedLog): void {
    const tradeId = log.args.tradeId?.toString() ?? '';
    if (!tradeId) return;

    const execution = this.closeExecutionFromMarketCloseExecuted(log);
    if (execution) {
      this.state.upsertCloseExecution(execution);
      return;
    }

    if (normalizeClosePercentage(log.args.percentageClosed) >= 100) {
      this.state.markTradeClosed(tradeId);
    }
  }

  private closeExecutionFromMarketCloseExecuted(log: MarketCloseExecutedLog): CloseExecution | undefined {
    const orderId = log.args.orderId?.toString();
    const tradeId = log.args.tradeId?.toString();
    if (!orderId || !tradeId) return undefined;

    const context = this.state.findTradeContext(tradeId);
    if (!context) return undefined;

    return buildCloseExecution({
      orderId,
      tradeId,
      trader: context.trader,
      pairId: context.pairId,
      pairTo: context.pairTo,
      pairFrom: context.pairFrom,
      pxRaw: log.args.price ?? 0n,
      percentProfitRaw: log.args.percentProfit ?? 0n,
      usdcSentToTraderRaw: log.args.usdcSentToTrader ?? 0n,
      percentageClosedRaw: log.args.percentageClosed,
      source: 'event',
      blockNumber: log.blockNumber,
    });
  }

  /**
   * Resolve which subscribed trader an optimistic open belongs to. When a single
   * address is streamed the choice is unambiguous; with multiple addresses the
   * caller must pass an explicit `user` that is part of the subscription.
   */
  private resolveOptimisticUser(user?: Address): Address {
    if (user) {
      if (!this.normalizedUsers.has(user.toLowerCase())) {
        throw new OstiumSubgraphError(
          `addOptimisticOpen user ${user} is not subscribed to this stream.`,
          OstiumSubgraphErrorCode.INVALID_PARAMS,
        );
      }
      return user;
    }
    if (this.normalizedUsers.size > 1) {
      throw new OstiumSubgraphError(
        'addOptimisticOpen requires an explicit user when streaming multiple addresses.',
        OstiumSubgraphErrorCode.INVALID_PARAMS,
      );
    }
    return this.defaultUser;
  }

  private optimisticTradeFromOpenParams(
    localId: string,
    params: OpenTradeParams,
    submission: SubmissionResult | undefined,
    trader: Address,
  ): AccountTradeItem {
    const pairId = String(params.pairId);
    const { trade } = buildTrade(
      {
        a: Number(params.pairId),
        b: params.buy,
        p: params.price,
        s: params.collateral,
        l: params.leverage,
        t: params.type,
        tp: params.takeProfit,
        sl: params.stopLoss,
        d: params.isDayTrade,
      },
      trader,
    );
    const pair = this.getRawPair(pairId) ??
      optimisticRawPair(pairId, trade.openPrice.toString(), this.options.getRawPairName(pairId));
    const leverage = BigInt(trade.leverage);
    const notional = (trade.collateral * leverage / 100n).toString();
    const rawTrade: RawTrade = {
      id: localId,
      tradeID: localId,
      trader,
      pair,
      isBuy: trade.buy,
      isDayTrade: trade.isDayTrade,
      isOpen: true,
      index: '-1',
      tradeType: 'Market',
      collateral: trade.collateral.toString(),
      notional,
      tradeNotional: tradeNotionalFromOpen(pair, trade.collateral, leverage, trade.openPrice),
      leverage: trade.leverage.toString(),
      highestLeverage: trade.leverage.toString(),
      openPrice: trade.openPrice.toString(),
      stopLossPrice: trade.sl.toString(),
      takeProfitPrice: trade.tp.toString(),
      rollover: rolloverBaselineAtOpen(pair, trade.buy, this.latestBlockNumber),
      timestamp: currentUnixSeconds(),
    };
    const confirmation: AccountTradeConfirmation = {
      localId,
      status: 'optimistic',
      initiatedTx: submission?.txHash,
      createdAt: Date.now(),
    };

    if (!this.getRawPair(pairId)) {
      void this.loadRawPairs().then(() => {
        if (!this.closed) void this.poll();
      });
    }

    return {
      keys: [localId],
      raw: rawTrade,
      value: formatConfirmedTrade(rawTrade, this.priceCache, this.latestBlockNumber, confirmation),
      confirmation,
    };
  }

  private handleMarketOpenExecuted(
    log: MarketOpenExecutedLog,
    retriedAfterPairLoad = false,
  ): void {
    const trade = log.args.t;
    if (!trade || !this.normalizedUsers.has(trade.trader.toLowerCase())) return;

    const pairId = trade.pairIndex.toString();
    const executedOrderId = log.args.orderId?.toString();
    if (executedOrderId && this.state.isTradeClosed(executedOrderId)) return;
    // Absorb any optimistic overlay entry for this trader+pair. Normally the
    // initiated event promotes it, but when that event is missed (WebSocket
    // reconnect gap) the optimistic entry would otherwise coexist with the
    // executed entry as a duplicate ghost that no trade-id tombstone can
    // reach. Promotion keys it by orderId, so the upsert below replaces it.
    if (executedOrderId) {
      this.state.promoteOptimisticTradeByPair(pairId, {
        orderId: executedOrderId,
        pairId,
        trader: trade.trader,
      });
    }
    const cachedPair = this.getRawPair(pairId);
    const pair = cachedPair ?? optimisticRawPair(
      pairId,
      trade.openPrice.toString(),
      this.options.getRawPairName(pairId),
    );

    if (!cachedPair && !retriedAfterPairLoad) {
      void this.loadRawPairs().then(() => {
        if (this.closed) return;
        this.handleMarketOpenExecuted(log, true);
      });
    }

    this.latestBlockNumber = log.blockNumber ?? this.latestBlockNumber;
    const rawTrade = this.rawTradeFromMarketOpenExecuted({
      orderId: log.args.orderId?.toString() ?? '',
      trade,
      tradeNotional: log.args.tradeNotional.toString(),
      pair,
      blockNumber: this.latestBlockNumber,
    });
    const orderId = log.args.orderId?.toString();
    const confirmation: AccountTradeConfirmation = {
      status: 'executed',
      orderId,
      createdAt: Date.now(),
    };
    const formatted = formatConfirmedTrade(
      rawTrade,
      this.priceCache,
      this.latestBlockNumber,
      confirmation,
    );

    this.state.upsertFastTrade(
      { keys: getTradeKeys(rawTrade), raw: rawTrade, value: formatted, confirmation },
      orderId,
    );

    if (!cachedPair && retriedAfterPairLoad) {
      void this.poll();
    }
  }

  private rawTradeFromMarketOpenExecuted({
    orderId,
    trade,
    tradeNotional,
    pair,
    blockNumber,
  }: {
    orderId: string;
    trade: MarketOpenExecutedTrade;
    tradeNotional: string;
    pair: RawPair;
    blockNumber: bigint | undefined;
  }): RawTrade {
    const leverage = BigInt(trade.leverage);
    const notional = (trade.collateral * leverage / 100n).toString();

    return {
      id: orderId,
      tradeID: orderId,
      trader: trade.trader,
      pair,
      isBuy: trade.buy,
      isDayTrade: trade.isDayTrade,
      isOpen: true,
      index: trade.index.toString(),
      tradeType: 'Market',
      collateral: trade.collateral.toString(),
      notional,
      tradeNotional,
      leverage: trade.leverage.toString(),
      highestLeverage: trade.leverage.toString(),
      openPrice: trade.openPrice.toString(),
      stopLossPrice: trade.sl.toString(),
      takeProfitPrice: trade.tp.toString(),
      rollover: rolloverBaselineAtOpen(pair, trade.buy, blockNumber),
      timestamp: currentUnixSeconds(),
    };
  }

  private getRawPair(pairId: string): RawPair | undefined {
    const cached = this.options.getRawPair(pairId);
    if (cached) return cached;

    return this.rawPairById?.get(pairId);
  }

  private async loadRawPairs(): Promise<void> {
    if (this.rawPairById) return;
    if (!this.rawPairsPromise) {
      this.rawPairsPromise = (async () => {
        try {
          const pairs = await this.options.fetchRawPairs();
          if (this.closed) return;
          this.rawPairById = new Map(pairs.map(pair => [String(pair.id), pair]));
        } catch (error) {
          this.emitError(error);
        } finally {
          this.rawPairsPromise = null;
        }
      })();
    }
    await this.rawPairsPromise;
  }

  private emit(snapshot: AccountUpdatesSnapshot): void {
    for (const handler of this.updateHandlers) handler(cloneSnapshot(snapshot));
  }

  private emitError(error: unknown): void {
    const normalized = error instanceof Error ? error : new Error(String(error));
    for (const handler of this.errorHandlers) handler(normalized);
  }
}
